import React from 'react';
import {
  YesNoQuestion,
  YesNoExplanationIfYesQuestion,
  QuestionAnswer
} from './questions/QuestionTypes';

// We need to create new question types for Number inputs
interface NumberQuestionProps {
  id: string;
  question: string;
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  required?: boolean;
  error?: string;
}

const NumberQuestionComponent: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      className="w-32 input"
      min="0"
      value={value.answer || ''}
      onChange={(e) => onChange({ ...value, answer: e.target.value })}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

interface Question9Data {
  [questionId: string]: QuestionAnswer;
}

interface Step3Question9Props {
  data: Question9Data;
  onChange: (data: Question9Data) => void;
  errors: Record<string, string>;
  selectedTimeline?: string;
  questionNumber?: number;
  questionTitle?: string;
  questionDescription?: string;
  currentSection?: number;
  totalSections?: number;
}

// Timeline-based questions for Question 9 (Observers & Complaints)
const TIMELINE_QUESTIONS = {
  morning: [
    // 9a
    { id: '9a', question: 'Political Party Representatives were able to fully observe the voting process and perform their duties', type: 'yes_no', required: true },
    // 9b
    { id: '9b', question: 'Various CSO observers were able to fully observe the voting process and perform their duties', type: 'yes_no', required: true },
    // 9c
    { id: '9c', question: 'Did you witness or hear of any complaints lodged at the polling Centre by observers and or anyone else?', type: 'yes_no_explanation_if_yes', required: true },
    // 9d - Conditional: Only show if 9c is "Yes"
    { id: '9d', question: 'How many complaints were lodged', type: 'number', required: true, conditional: { dependsOn: '9c', showWhen: 'Yes' } }
  ],
  afternoon: [
    // 19a
    { id: '19a', question: 'Political Party Representatives were able to fully observe the voting process and perform their duties', type: 'yes_no', required: true },
    // 19b
    { id: '19b', question: 'Various CSO observers were able to fully observe the voting process and perform their duties', type: 'yes_no', required: true },
    // 19c
    { id: '19c', question: 'Did you witness or hear of any complaints lodged at the polling Centre by observers and or anyone else?', type: 'yes_no', required: true },
    // 19c.1 - Conditional: Only show if 19c is "Yes"
    { id: '19c1', question: 'If yes to 19c: How many complaints were lodged?', type: 'number', required: true, conditional: { dependsOn: '19c', showWhen: 'Yes' } },
    // 19d
    { id: '19d', question: 'Have you witnessed or heard any persons or people who have been ferried from another area to vote at the Centre you observed?', type: 'yes_no', required: true }
  ],
  vote_count: []
};

const QUESTION_COMPONENTS = {
  'yes_no': YesNoQuestion,
  'yes_no_explanation_if_yes': YesNoExplanationIfYesQuestion,
  'number': NumberQuestionComponent,
};

const Step3Question9: React.FC<Step3Question9Props> = ({ data, onChange, errors, selectedTimeline }) => {
  const updateQuestion = (questionId: string, value: QuestionAnswer) => {
    onChange({
      ...data,
      [questionId]: value
    });
  };

  // Get questions for the selected timeline with conditional filtering
  const getQuestionsForTimeline = () => {
    if (!selectedTimeline || !TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS]) {
      return [];
    }

    const allQuestions = TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS];

    // Filter out conditional questions that shouldn't be shown
    return allQuestions.filter(question => {
      if (!question.conditional) return true;

      const dependentAnswer = data?.[question.conditional.dependsOn]?.answer;
      return dependentAnswer === question.conditional.showWhen;
    });
  };

  const questions = getQuestionsForTimeline();

  const getCompletionStatus = () => {
    const completed = questions.filter(q => data?.[q.id]?.answer).length;
    return { completed, total: questions.length };
  };

  const { completed, total } = getCompletionStatus();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Question 9: Observers & Complaints
        </h2>
        <p className="mb-4 text-gray-600">
          Timeline: {selectedTimeline ? selectedTimeline.charAt(0).toUpperCase() + selectedTimeline.slice(1).replace('_', ' ') : 'No timeline selected'}
        </p>
        {!selectedTimeline && (
          <div className="p-4 mb-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-yellow-800">Please select a timeline in Step 1 to see relevant questions for Question 9.</p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="p-3 mb-6 border border-red-200 rounded-lg bg-red-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-red-900">Question 9 Progress</span>
            <span className="text-sm text-red-800">{completed}/{total} completed</span>
          </div>
          <div className="h-2 mt-2 bg-red-200 rounded-full">
            <div
              className="h-2 transition-all duration-300 bg-red-600 rounded-full"
              style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.length > 0 ? questions.map((question) => {
          const QuestionComponent = QUESTION_COMPONENTS[question.type as keyof typeof QUESTION_COMPONENTS];
          const questionValue = data?.[question.id] || {};
          const questionError = errors?.[question.id];

          return (
            <div key={question.id} className="p-4 transition-colors bg-white border border-gray-200 rounded-lg hover:border-red-300">
              <QuestionComponent
                id={question.id}
                question={`${question.id}: ${question.question}`}
                value={questionValue}
                onChange={(value) => updateQuestion(question.id, value)}
                required={question.required}
                error={questionError}
              />
            </div>
          );
        }) : (
          <div className="py-8 text-center text-gray-500">
            {selectedTimeline ? 'No questions available for this timeline.' : 'Select a timeline to view questions.'}
          </div>
        )}
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 className="mb-2 font-medium text-red-900">Please complete the following required questions:</h4>
          <ul className="space-y-1 text-sm text-red-800">
            {Object.entries(errors).map(([questionId, error]) => (
              <li key={questionId}>• {questionId}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Period Summary */}
      <div className="p-4 rounded-lg bg-gray-50">
        <h4 className="mb-2 font-medium text-gray-900"> Summary</h4>
        <p className="text-sm text-gray-700">
          This section covers observers and complaints at the polling station including party observers,
          CSO observers, and any complaints that were lodged.
        </p>
        <div className="mt-2 text-sm text-gray-600">
          Completion: {completed}/{total} questions ({total > 0 ? Math.round((completed / total) * 100) : 0}%)
        </div>
      </div>
    </div>
  );
};

export default Step3Question9;